import React from 'react';
import { createRoot } from 'react-dom/client';
import './index.css'; // Import Tailwind CSS
import App from './App';
import ErrorBoundary from './components/ErrorBoundary';
import { installGlobalErrorHandlers } from './services/clientErrorService';
import { healIfStale, getRunningMainBundle } from './utils/buildVersion';
import { initTheme } from './theme/theme';

// Stamp the saved theme on <html> before first paint so dark-mode users
// don't get a white flash. Light remains the default.
initTheme();

// Suppress verbose console output in production builds (audit 2026-05-17).
// The bundle still contains the log strings (a follow-up CRACO/terser
// `drop_console` pass can strip them), but no log/debug/info/warn statements
// will fire at runtime so sensitive context such as autosave keys, user IDs,
// FX cache lookups, microphone status etc. no longer leak via DevTools.
// `console.error` is preserved on purpose so genuine errors remain visible.
if (process.env.NODE_ENV === 'production') {
  ['log', 'debug', 'info', 'warn'].forEach((method) => {
    // eslint-disable-next-line no-console
    console[method] = () => {};
  });
}

// Errors thrown outside the React tree (event handlers, timers, rejected
// promises) are invisible to an error boundary. Installed before render so a
// fault during boot is still recorded. See services/clientErrorService.js.
installGlobalErrorHandlers({ getBundle: getRunningMainBundle });

// If this tab is running a bundle older than the one the server is serving, it
// is about to fail against contracts that have moved underneath it — reload once
// onto the current build before the user ever sees a broken screen. Resolves to
// a no-op whenever freshness cannot be established (dev server, offline).
// Incident 2026-08-21: see utils/buildVersion.js for the full story.
healIfStale();

const container = document.getElementById('root');
const root = createRoot(container);

root.render(
  <React.StrictMode>
    <ErrorBoundary boundary="root">
      <App />
    </ErrorBoundary>
  </React.StrictMode>
);